import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { meKeys, quoteQueryKeys, todayKeys, topKeys } from '@/lib/api/query-keys'
import type { GroupDTO, QuoteDTO } from '@/types/api'
import type { MeDTO, TodayDTO, TopDTO } from '@/types/api-v'

// Single source of truth for the data each bottom-nav tab shows first. The
// screens and the prefetcher must share the exact queryKey + queryFn, or the
// prefetched entry is never hit. Defaults mirror the screens' initial state
// (archive: top/week/no search, top: authors/week).

export const groupsQuery = () =>
  queryOptions({
    queryKey: ['groups'],
    queryFn: () => api<{ groups: GroupDTO[] }>('/api/groups'),
  })

export const todayQuery = (groupId: string) =>
  queryOptions({
    queryKey: todayKeys.today(groupId),
    queryFn: () => api<TodayDTO>(`/api/groups/${groupId}/today`),
  })

export const meQuery = (groupId: string) =>
  queryOptions({
    queryKey: meKeys.me(groupId),
    queryFn: () => api<MeDTO>(`/api/groups/${groupId}/me`),
  })

export const topQuery = (groupId: string, board = 'authors', period = 'week') =>
  queryOptions({
    queryKey: topKeys.board(groupId, board, period),
    queryFn: () => api<TopDTO>(`/api/groups/${groupId}/top?board=${board}&period=${period}`),
  })

export const archiveQuery = (groupId: string, sort = 'top', period = 'week', q = '') =>
  infiniteQueryOptions({
    queryKey: quoteQueryKeys.archive(groupId, sort, period, q),
    queryFn: ({ pageParam }) => {
      const sp = new URLSearchParams({ sort, period, limit: '30' })
      if (q) sp.set('q', q)
      if (pageParam) sp.set('cursor', pageParam)
      return api<{ items: QuoteDTO[]; nextCursor: string | null }>(`/api/groups/${groupId}/quotes?${sp}`)
    },
    initialPageParam: '',
    getNextPageParam: (last: { nextCursor: string | null }) => (sort === 'random' ? null : last.nextCursor),
  })
